/**
 * Epson ePOS-Print SOAP client (#1005 D).
 *
 * Talks directly to a TM-T88V / TM-m30 series printer's built-in HTTP endpoint
 * (`cgi-bin/epos/service.cgi`) from the browser. No native app, no Pi, no
 * server-side daemon. The store owner's tablet loads YOKOSO PAY on the same
 * LAN as the printer and calls `sendEscposBase64` when a new kitchen ticket
 * is ready.
 *
 * Wire format: minimal SOAP envelope around `<epos-print><command>HEX</command>`
 * with hex-encoded ESC/POS bytes. The printer's firmware responds with an XML
 * envelope carrying `success="true|false"` on the `<response>` element.
 *
 * ## HTTPS / mixed-content note
 *
 * YOKOSO PAY is served over HTTPS. Browsers block `fetch` from HTTPS pages to
 * `http://...` targets ("mixed content"). Two workarounds documented in the
 * setup guide:
 *   1. Enable HTTPS on the printer (Epson firmware supports self-signed cert)
 *      and install the cert on the tablet. Most robust; setup per store ~5-10 min.
 *   2. As a fallback for pure emulator / local dev, YOKOSO PAY can be served
 *      over HTTP for the tablet page (not for production).
 *
 * The functions here are transport-agnostic; the caller supplies the printer
 * URL scheme.
 */

export interface EposPrintOptions {
  /** Printer base URL. Example: `http://192.168.0.50` or `https://192.168.0.50`. Trailing slash tolerated. */
  printerUrl: string
  /** Device id path parameter. TM-T88V embedded LAN uses `local_printer` by default. */
  devid?: string
  /** Printer-side timeout in ms. Passed as query param; also enforced client-side. */
  timeoutMs?: number
}

export type PrintResult =
  | { ok: true; status: number }
  | { ok: false; error: string; code?: string }

/**
 * Send a base64-encoded ESC/POS payload (as returned by
 * `GET /orders/{id}/kitchen-ticket-preview.escposBase64`) to the printer.
 * Returns success/error rather than throwing so the caller can retry.
 */
export async function sendEscposBase64(
  escposBase64: string,
  opts: EposPrintOptions,
): Promise<PrintResult> {
  const bytes = base64ToBytes(escposBase64)
  return sendEscposBytes(bytes, opts)
}

/** Underlying send that accepts raw bytes. Used by test-print helpers. */
export async function sendEscposBytes(
  bytes: Uint8Array,
  opts: EposPrintOptions,
): Promise<PrintResult> {
  const hex = bytesToHex(bytes)
  const soap = buildSoap(hex)
  const url = buildUrl(opts)
  const timeout = opts.timeoutMs ?? 10000

  const controller = new AbortController()
  const timerId = setTimeout(() => controller.abort(), timeout + 1000)
  let res: Response
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'text/xml; charset=utf-8',
        // Epson SOAP quirks: some firmware wants this to disable caching.
        'If-Modified-Since': 'Thu, 01 Jan 1970 00:00:00 GMT',
        SOAPAction: '""',
      },
      body: soap,
      signal: controller.signal,
    })
  } catch (e) {
    const err = e as Error
    if (err.name === 'AbortError') return { ok: false, error: 'timeout' }
    return { ok: false, error: `network: ${err.message}` }
  } finally {
    clearTimeout(timerId)
  }

  if (!res.ok) return { ok: false, error: `HTTP ${res.status}` }
  const text = await res.text()
  return parseResponse(text)
}

/**
 * Print a fixed "*** ePOS-Print TEST ***" ticket. Used by the "テスト印刷"
 * button in the store owner UI to verify LAN + firmware + config without
 * needing a real order.
 */
export async function testPrint(opts: EposPrintOptions): Promise<PrintResult> {
  const text = new TextEncoder().encode('*** ePOS-Print TEST ***\nYOKOSO PAY\n')
  const bytes = new Uint8Array([
    0x1b,
    0x40, // ESC @ — initialize
    ...text,
    0x1b,
    0x64,
    0x04, // ESC d 4 — feed 4 lines
    0x1d,
    0x56,
    0x42,
    0x00, // GS V B 0 — partial cut
  ])
  return sendEscposBytes(bytes, opts)
}

// -- internals ---------------------------------------------------------------

function buildUrl(opts: EposPrintOptions): string {
  const base = normalizeBase(opts.printerUrl)
  const devid = opts.devid ?? 'local_printer'
  const timeout = opts.timeoutMs ?? 10000
  return `${base}/cgi-bin/epos/service.cgi?devid=${encodeURIComponent(devid)}&timeout=${timeout}`
}

function buildSoap(hex: string): string {
  // Minimal SOAP envelope. `<command>` accepts a hex-encoded byte stream —
  // more universally supported by Epson firmware than the higher-level
  // `<text>`/`<image>` tags, which have per-model quirks.
  return (
    '<?xml version="1.0" encoding="utf-8"?>' +
    '<s:Envelope xmlns:s="http://schemas.xmlsoap.org/soap/envelope/">' +
    '<s:Body>' +
    '<epos-print xmlns="http://www.epson-pos.com/schemas/2011/03/epos-print">' +
    `<command>${hex}</command>` +
    '</epos-print>' +
    '</s:Body>' +
    '</s:Envelope>'
  )
}

function parseResponse(xml: string): PrintResult {
  // <response ... success="true" status="XXXX" code=""/>
  const success = /\ssuccess="true"/i.test(xml)
  if (success) {
    const m = xml.match(/\sstatus="(\d+)"/i)
    return { ok: true, status: m ? Number(m[1]) : 0 }
  }
  const codeMatch = xml.match(/\scode="([^"]*)"/i)
  return {
    ok: false,
    error: 'printer reported error',
    code: codeMatch?.[1] ?? undefined,
  }
}

function base64ToBytes(b64: string): Uint8Array {
  const bin = atob(b64)
  const out = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i)
  return out
}

function bytesToHex(bytes: Uint8Array): string {
  let out = ''
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0')
  }
  return out
}

/**
 * プリンター URL を正規化する。前後の空白を除去し、末尾スラッシュを **全て** 落とす。
 *
 * 入力欄（StoreKitchen）は値をそのまま保存するため、`https://192.168.100.3/ `
 * のように **末尾スペース** が混入することがある。旧 `trimTrailingSlash` は
 * `endsWith('/')` を見ていたため、末尾スペースがあるとスラッシュを落とせず、
 * `${base}/cgi-bin` が `.../ /cgi-bin`（= URL 上 `%20`）になり印刷が壊れていた。
 * `.trim()` を先に掛け、末尾スラッシュは `/+$` で全て除去する。
 */
function normalizeBase(s: string): string {
  return s.trim().replace(/\/+$/, '')
}
